"use client";
import React from "react";
import Select, { Option } from "./select";
import Button from "./button";
import Icon from "./icon";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import * as ScrollArea from "@radix-ui/react-scroll-area";

export type PageSize = (typeof PAGE_SIZES)[number];
export const PAGE_SIZES: number[] = [10, 20, 25, 50, 100];
export const PAGE_SIZE_OPTIONS: Option[] = PAGE_SIZES.map((size) => ({
  label: size.toString(),
  value: size.toString(),
}));

export interface TablePaginationProps {
  page: number;
  size: number;
  count: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
}

export default function TablePagination(props: TablePaginationProps) {
  const totalPages = Math.ceil(props.count / props.size);
  const startRow = (props.page - 1) * props.size + 1;
  const endRow =
    startRow + props.size - 1 > props.count
      ? props.count
      : startRow + props.size - 1;
  const overflowContainerRef = React.useRef<HTMLDivElement>(null);

  // scroll to the right on mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (overflowContainerRef.current) {
        overflowContainerRef.current.scrollLeft =
          overflowContainerRef.current.scrollWidth -
          overflowContainerRef.current.clientWidth;
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  function changePageSize(value: string) {
    props.onPageSizeChange(parseInt(value));
  }

  function goNext() {
    if (props.page < totalPages) {
      props.onPageChange(props.page + 1);
    }
  }

  function goPrev() {
    if (props.page > 1) {
      props.onPageChange(props.page - 1);
    }
  }
  return (
    <ScrollArea.Root>
      <ScrollArea.Viewport ref={overflowContainerRef}>
        <div className="mt-4 flex whitespace-nowrap gap-2 flex-row items-end desktop-grid:items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <div className="text-body-regular">Rows per page</div>
            <Select
              variant="default"
              label="Page size"
              value={`${props.size}`}
              options={PAGE_SIZE_OPTIONS}
              onValueChange={(v) => changePageSize(v)}
            />
          </div>
          <div className="flex flex-row items-center gap-4 text-body-regular">
            <div className="flex flex-row">
              <span className="text-secondary-300">
                {startRow}-{endRow}
              </span>
              &nbsp;
              <span className="text-secondary-500">of {props.count}</span>
            </div>
            <div className="flex flex-row gap-1">
              <Button
                variant="circle"
                color="tertiary"
                size="sm"
                onClick={goPrev}
              >
                <Icon name="arrow-left" size="sm" />
              </Button>
              <Button
                variant="circle"
                color="tertiary"
                size="sm"
                onClick={goNext}
              >
                <Icon name="arrow-right" size="sm" />
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="horizontal"
        className="flex select-none touch-none bg-transparent transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-1"
      >
        <ScrollArea.Thumb className="flex-1 bg-secondary-500 rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
      </ScrollArea.Scrollbar>
      <ScrollArea.Corner />
    </ScrollArea.Root>
  );
}
